<template>
  <responsive-drawer-layout v-if="isLoaded" :hasDrawer="true">
    <template v-slot:drawer>
      <v-card-text v-if="getUniqueTags.length > 0">
        <h2 class="text-h6 mb-2">
          {{ $t('title.kat.choice') }}
        </h2>
        <v-chip-group column multiple v-model="tags">
          <v-chip :color="tags.includes(tag) ? theme.elements.btnColor : null" filter outlined :value="tag" v-for="tag in getUniqueTags" :key="tag">
            {{ tag }}
          </v-chip>
        </v-chip-group>
      </v-card-text>

      <v-card-text v-if="getUniqueDepartments.length > 0">
        <h2 class="text-h6 mb-2">
          {{ $t('title.dep.choice') }}
        </h2>
        <v-chip-group column multiple v-model="departments">
          <v-chip
            :color="departments.includes(department) ? theme.elements.btnColor : null"
            filter
            outlined
            v-for="(department, ind) in getUniqueDepartments"
            :key="ind"
            :value="department"
          >
            {{ department }}
          </v-chip>
        </v-chip-group>
      </v-card-text>

      <v-card-text v-if="getUniquePositions.length > 0">
        <h2 class="text-h6 mb-2">
          {{ $t('title.pos.choice') }}
        </h2>
        <v-chip-group column multiple v-model="positions">
          <v-chip
            :color="positions.includes(position) ? theme.elements.btnColor : null"
            filter
            outlined
            v-for="(position, ind) in getUniquePositions"
            :key="ind"
            :value="position"
          >
            {{ position }}
          </v-chip>
        </v-chip-group>
      </v-card-text>

      <v-card-text v-if="getUniqueCompanies.length > 0">
        <h2 class="text-h6 mb-2">
          {{ $t('title.comp.choice') }}
        </h2>
        <v-chip-group column multiple v-model="companies">
          <v-chip
            :color="companies.includes(company) ? theme.elements.btnColor : null"
            filter
            outlined
            v-for="(company, ind) in getUniqueCompanies"
            :key="ind"
            :value="company"
          >
            {{ company }}
          </v-chip>
        </v-chip-group>
      </v-card-text>

      <div class="fab-buttons-row">
        <v-btn rounded color="primary" @click="resetChips">{{ $t('resetFilter') }}</v-btn>
        <v-btn @click="mx_isLeftDrawerOpen = false" class="ml-2 hidden-sm-and-up" color="primary" rounded>
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </template>
    <!-- Page content -->
    <template v-slot:content>
      <div class="result-container-wrapper">
        <div class="result-container">
          <v-text-field
            solo
            hide-details
            prepend-inner-icon="mdi-magnify"
            class="field mx-2 ma-sm-1"
            v-model="search"
            rounded
            clearable
          ></v-text-field>
          <div>
            <v-btn-toggle v-model="employeeViewType" class="ma-sm-1" mandatory tile group>
              <v-btn value="list" outlined color="primary">
                <v-icon>mdi-format-list-bulleted-square</v-icon>
              </v-btn>
              <v-btn value="card" outlined color="primary">
                <v-icon>mdi-account-box-multiple</v-icon>
              </v-btn>
            </v-btn-toggle>
          </div>
        </div>
        <employee-list-view
          v-if="employeeViewType === 'list'"
          :filtered-person="filteredPerson"
          :photo-by-employee-id="photoByEmployeeId"
        ></employee-list-view>
        <employee-card-view
          v-if="employeeViewType === 'card'"
          :photo-by-employee-id="photoByEmployeeId"
          :filtered-person="filteredPerson"
        ></employee-card-view>
      </div>
      <!-- loading display -->
      <info-card v-if="!isLoaded" :textRows="[$t('isLoading.moduleIsGettingLoaded')]">
        <template v-slot:title>{{ $t('isLoading.title') }}</template>
      </info-card>
    </template>
  </responsive-drawer-layout>
</template>
<script>
import { mapState } from 'vuex';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';
import dateTimeMixin from '@/mixins/dateTimeMixin';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';
import InfoCard from '@/components/container/InfoCard';
import employeeService from '@/modules/employee/employeeService';
import EmployeeListView from '@/modules/employee/EmployeeListView.vue';
import employeeCardView from '@/modules/employee/EmployeeCardView.vue';

export default {
  name: 'Employee',
  mixins: [common, configMixin, dateTimeMixin],

  components: {
    employeeCardView,
    ResponsiveDrawerLayout,
    InfoCard,
    EmployeeListView,
  },

  data() {
    return {
      search: '',
      departments: [],
      tags: [],
      companies: [],
      positions: [],
      clickedDepartments: false,
      employeeViewType: JSON.parse(localStorage.getItem('userProfile')).employeeViewType || 'list',
    };
  },
  computed: {
    ...mapState('employee', ['isLoaded', 'personList', 'photoByEmployeeId']),

    /**
     * The getUniqueTags Function extracts the unique tags from the persons and returns them filtered alphabetically.
     * @returns {*[]}
     */
    getUniqueTags() {
      let tags = [];
      this.personList.forEach((person) => {
        if (person.tags) {
          tags = tags.concat(person.tags);
        }
      });
      let filteredUniqueTags = tags.filter((item, index) => tags.indexOf(item) === index);
      return filteredUniqueTags.sort();
    },

    /**
     * The getUniqueDepartments function extracts the departments from the persons and returns the index of each department.
     * @returns {*[]}
     */
    getUniqueDepartments() {
      let departments = [];
      this.personList.forEach((person) => {
        if (person.department) {
          departments.push(person.department);
        }
      });
      let filteredUniqueDepartments = departments.filter((item, index) => departments.indexOf(item) === index);
      return filteredUniqueDepartments.sort();
    },

    /**
     * The getUniquePositions function ectracts the positions from the persons Array and returns the index of each position.
     * @returns {*[]}
     */
    getUniquePositions() {
      let positions = [];

      this.personList.forEach((person) => {
        if (person.position) {
          positions.push(person.position);
        }
      });
      let filteredUniquePositions = positions.filter((item, index) => positions.indexOf(item) === index);
      return filteredUniquePositions.sort();
    },

    /**
     * The getUniqueCompanies Function extracts the companies from the persons and returns the index of each company
     * @returns Companies sorted Alphabetically[]
     */
    getUniqueCompanies() {
      // über den Array von Personen iterieren und die Firmennamen und Namen von jeder Person auslesen
      let companies = [];
      this.personList.forEach((person) => {
        if (person.company) {
          companies = companies.concat(person.company);
        }
      });
      // von den ausgelesenen Firmen den Index suchen und einen Array mit den unique Company names zurückgeben
      let filteredUniqueCompanies = companies.filter((item, index) => companies.indexOf(item) === index);
      return filteredUniqueCompanies.sort();
    },

    /**
     * This Function return the filtered Person
     * @returns {*}
     */
    filteredPerson() {
      let search = this.search;

      let filteredPersonList;
      if (!search) {
        filteredPersonList = this.personList;
      }
      filteredPersonList = this.personList.filter((item) => {
        return employeeService.isSearchTextIncludedInPersonAttributes({
          item,
          search,
        });
      });

      return employeeService.filterPersonsByCatagories(filteredPersonList, this.departments, this.positions, this.tags, this.companies);
    },
  },
  methods: {
    resetChips() {
      this.departments = [];
      this.tags = [];
      this.companies = [];
      this.positions = [];
    },
  },
  watch: {
    employeeViewType(newValue, oldValue) {
      let userProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      userProfile.employeeViewType = newValue;
      localStorage.setItem('userProfile', JSON.stringify(userProfile));
    },
  },
};
</script>
<style>
.fab-buttons-row {
  position: fixed;
  bottom: 10px;
  display: flex;
  left: 30px;
}

@media (min-width: 600px) {
  .result-container-wrapper {
    margin: 12px;
  }
}

.result-container-wrapper {
  margin: 0;
  padding: 0 0 80px;
  height: inherit;
}

.theme--dark .result-container-wrapper {
  background: #101010;
}

.theme--light .result-container-wrapper {
  background: #fafafa;
}

.result-container {
  display: flex;
  padding-top: 10px;
}
</style>